export type CartShippingLine =
    | { kind: 'free'; strikethroughCost: number | null }
    | { kind: 'charged'; cost: number }
    | { kind: 'calculatedAtCheckout' }

export interface CartSummary {
    shipping: CartShippingLine
    listPrice: number
    subtotal: number
    hasShippingSavings: boolean
}

interface CartSummaryInput {
    itemsListPrice: number
    itemsTotal: number
    freeShippingEnabled: boolean
    freeShippingMinCartAmount: number
    freeShippingTypicalCost: number | null
    freeShippingCostInSubtotal: boolean
}

/**
 * The free-shipping threshold is measured on the discounted items total;
 * a minimum of 0 means shipping is always free.
 */
export const getCartSummary = ({
    itemsListPrice,
    itemsTotal,
    freeShippingEnabled,
    freeShippingMinCartAmount,
    freeShippingTypicalCost,
    freeShippingCostInSubtotal,
}: CartSummaryInput): CartSummary => {
    const typicalCost =
        freeShippingTypicalCost != null && freeShippingTypicalCost > 0
            ? freeShippingTypicalCost
            : null
    const includeShipping =
        freeShippingEnabled &&
        freeShippingCostInSubtotal &&
        typicalCost !== null
    const qualifiesForFreeShipping =
        freeShippingEnabled &&
        (freeShippingMinCartAmount === 0 ||
            itemsTotal >= freeShippingMinCartAmount)

    if (qualifiesForFreeShipping) {
        return {
            shipping: { kind: 'free', strikethroughCost: typicalCost },
            listPrice: itemsListPrice + (includeShipping ? typicalCost : 0),
            subtotal: itemsTotal,
            hasShippingSavings: includeShipping,
        }
    }

    if (!includeShipping) {
        return {
            shipping: { kind: 'calculatedAtCheckout' },
            listPrice: itemsListPrice,
            subtotal: itemsTotal,
            hasShippingSavings: false,
        }
    }

    return {
        shipping: { kind: 'charged', cost: typicalCost },
        listPrice: itemsListPrice + typicalCost,
        subtotal: itemsTotal + typicalCost,
        hasShippingSavings: false,
    }
}
